// @ts-strict-ignore
import { cloneDeep } from 'lodash/fp';

import {
  ActionType,
  WorkflowTransformationIssueType,
} from '../constants';
import buildDependencies from '../dependencies';
import type {
  SchemaInstance,
  Action,
  Tag,
  DataRoute,
  Threshold,
  Rules,
  List,
  EngineValidationData,
  Expression,
  Outcome,
  Services,
  Attribute,
  ReasonCodeGroup,
  ServicesByName,
} from '../types';
import { generatePublicIdHash, remapListIdsForListServiceAction } from '../utils';
import type { WorkflowState } from '../../redux/state';
import { remapActionOptionValues, remapIdsForThresholdsAndDataRoutes } from './helpers/mapping';
import { getOutcomeByName } from '../../../service/outcomes/outcomes';
import { isActionOfType } from 'flow/service/actions';

// Helper function to collect matrix model RHS issues
function collectMatrixModelRHSIssues(
  thresholds: Record<string, Threshold>,
  actions: Record<string, Action>,
) {
  return Object.entries(thresholds)
    .filter(([, t]) => t.right.actionId && actions[t.right.actionId])
    .filter(([, t]) => isActionOfType(actions[t.right.actionId], [
      ActionType.MATRIX_MODEL, ActionType.IMPORTED_MATRIX_MODEL,
    ]))
    .map(([id, t]) => ({
      publicId: id,
      manualResolution: false,
      type: WorkflowTransformationIssueType.MATRIX_MODEL_NOT_SUPPORTED_AS_RHS_ACTION,
      data: t,
    }));
}

function generateMissingObjectsInTheOrg({
  action,
  tags,
  outcomes,
  services,
}: {
  action: Action;
  services: Services;
  tags: Tag[];
  outcomes: Outcome[];
}) {
  const identifiedObjectsMissingInTheOrganization = [];

  if (isActionOfType(action, ActionType.TAG)) {
    const matchingTagObject = tags.find(
      tag => tag.tag_name.toLowerCase() === action.tag.name.toLowerCase());
    if (!matchingTagObject || (matchingTagObject && matchingTagObject.archived)) {
      if (action.tag.outcomes?.length > 0) {
        action.tag.outcomes.forEach(tagOutcome => {
          const matchingTagOutcomeObject = getOutcomeByName(outcomes, tagOutcome.outcome);
          if (!matchingTagOutcomeObject
            || (matchingTagOutcomeObject && matchingTagOutcomeObject.archived)) {
            if (!identifiedObjectsMissingInTheOrganization.find(
              object => object.type === 'outcome' && object.name === tagOutcome.outcome)) {
              identifiedObjectsMissingInTheOrganization.push({
                type: 'outcome',
                name: tagOutcome.outcome,
                status: matchingTagOutcomeObject?.archived ? 'archived' : '',
                data: { name: tagOutcome.outcome },
              });
            }
          }
        });
      }
      identifiedObjectsMissingInTheOrganization.push({
        type: 'tag',
        name: action.tag.name,
        status: matchingTagObject?.archived ? 'archived' : '',
        data: action.tag,
      });
    }
  }

  if (isActionOfType(action, ActionType.OUTCOME)) {
    const matchingOutcomeObject = getOutcomeByName(outcomes, action.outcome.name);
    if (!matchingOutcomeObject || (matchingOutcomeObject && matchingOutcomeObject.archived)) {
      if (!identifiedObjectsMissingInTheOrganization.find(
        object => object.type === 'outcome' && object.name === action.outcome.name)) {
        identifiedObjectsMissingInTheOrganization.push({
          type: 'outcome',
          name: action.outcome.name,
          status: matchingOutcomeObject?.archived ? 'archived' : '',
          data: action.outcome,
        });
      }
    }
  }

  if (isActionOfType(action, ActionType.SERVICE)) {
    const matchingServiceObject = Object.entries(services).find(
      service => service[1].internal_name === action.service.internalName && service[1].enabled);
    if (!matchingServiceObject) {
      identifiedObjectsMissingInTheOrganization.push({
        type: 'service',
        name: action.service.name,
        status: 'disabled',
        data: action.service,
      });
    }
  }

  return identifiedObjectsMissingInTheOrganization;
}

function remapIdsAndIdentifyMissingObjectsBeforeImport({
  jsonData,
  services,
  tags,
  outcomes,
  reasonCodeGroups,
  lists,
}: {
  jsonData: Partial<SchemaInstance & {
    serviceAttributeData: Record<number, {
      service: string;
      attribute: Attribute;
    }>;
    reasonCodeGroupData: Record<number, ReasonCodeGroup>;
  }>;
  services: ServicesByName;
  tags: Tag[];
  outcomes: Outcome[];
  reasonCodeGroups: ReasonCodeGroup[];
  lists: List[];
}) {
  const issuesWithIncomingData: EngineValidationData = {
    actions: [],
    thresholds: [],
  };
  let identifiedObjectsMissingInTheOrganization = [];
  const actions = Object.entries(jsonData.actions);
  const updatedActionsForImport: Record<string, Action> = {};
  const actionPublicIdReassignmentMap = {};

  actions.forEach(([actionKey, actionValue]) => {
    const newPublicId = `action__${actionValue.type}__${generatePublicIdHash(20)}`;
    actionPublicIdReassignmentMap[actionKey] = newPublicId;
    actionValue.publicId = newPublicId;

    const missingObject = generateMissingObjectsInTheOrg({
      action: actionValue,
      tags,
      outcomes,
      services,
    });

    if (isActionOfType(actionValue, ActionType.OUTPUT) && !actionValue.output.dataType) {
      actionValue.output.dataType = 'decimal';
    }

    if (missingObject) {
      identifiedObjectsMissingInTheOrganization = [
        ...identifiedObjectsMissingInTheOrganization,
        ...missingObject,
      ];
    }

    if (actionValue.options) {
      const { actionOption, issues } = remapActionOptionValues({
        actionOption: actionValue.options,
        action: actionValue,
        lists,
      });

      actionValue.options = actionOption;
      if (issues.length) {
        issuesWithIncomingData.actions = [
          ...issuesWithIncomingData.actions,
          ...issues,
        ];
      }
    }

    updatedActionsForImport[newPublicId] = actionValue;
  });

  const {
    updatedActionsForImportWithRemappedRuleExpressions,
    thresholdsWithReassignedPublicId,
    dataRoutesWithReassignedPublicId,
    identifiedAttributesMissingInTheOrganization,
    issuesWithIncomingThresholds,
  } = remapIdsForThresholdsAndDataRoutes({
    thresholds: Object.entries(jsonData.thresholds),
    dataRoutes: Object.entries(jsonData.dataRoutes),
    serviceAttributeData: jsonData.serviceAttributeData,
    services,
    reasonCodeGroups,
    reasonCodeGroupData: jsonData.reasonCodeGroupData,
    actionPublicIdReassignmentMap,
    updatedActionsForImport,
  });

  // Validate matrix model RHS actions in imported thresholds
  const matrixModelRHSIssues = collectMatrixModelRHSIssues(
    thresholdsWithReassignedPublicId,
    updatedActionsForImport,
  );

  issuesWithIncomingData.thresholds = [...issuesWithIncomingThresholds, ...matrixModelRHSIssues];
  identifiedObjectsMissingInTheOrganization = [
    ...identifiedObjectsMissingInTheOrganization,
    ...identifiedAttributesMissingInTheOrganization,
  ];
  identifiedObjectsMissingInTheOrganization.sort(
    (a, b) => {
      if (a.type === 'outcome') return -1;
      if (b.type === 'outcome') return 1;
      return 0;
    });

  return {
    actions: updatedActionsForImportWithRemappedRuleExpressions,
    thresholds: thresholdsWithReassignedPublicId,
    dataRoutes: dataRoutesWithReassignedPublicId,
    identifiedObjectsMissingInTheOrganization,
    issues: issuesWithIncomingData,
  };
}

export function generateImportableWorkflowJson({
  jsonData,
  services,
  tags,
  outcomes,
  reasonCodeGroups,
  lists,
}: {
  jsonData: Partial<SchemaInstance>;
  services: ServicesByName;
  tags: Tag[];
  outcomes: Outcome[];
  reasonCodeGroups: ReasonCodeGroup[];
  lists: List[];
}) {
  if (jsonData.token) {
    // we'll consider this is a full workflow data copied from elsewhere
    // later we'll support a case of json not having a workflow token; that will be a rule module
    const {
      actions,
      thresholds,
      dataRoutes,
      identifiedObjectsMissingInTheOrganization,
      issues,
    } = remapIdsAndIdentifyMissingObjectsBeforeImport({
      jsonData,
      services,
      tags,
      outcomes,
      reasonCodeGroups,
      lists,
    });

    const regeneratedDependencies = buildDependencies({
      actions,
      dataRoutes,
      thresholds,
    });

    return {
      actions,
      thresholds,
      dataRoutes,
      dependencies: regeneratedDependencies,
      identifiedObjectsMissingInTheOrganization,
      issues,
    };
  }

  return {
    actions: {},
    thresholds: {},
    dataRoutes: {},
    dependencies: {},
    issues: { actions: [], thresholds: [] },
  };
}

export function partialImportIntoWorkflowJson({
  jsonData,
  workflowData,
  services,
  tags,
  outcomes,
  reasonCodeGroups,
  lists,
}: {
  jsonData: {
    actions: Record<string, Action>;
    thresholds: Record<string, Threshold>;
    dataRoutes: Record<string, DataRoute>;
    serviceAttributeData: Record<number, {
      service: string;
      attribute: Attribute;
    }>;
    reasonCodeGroupData: Record<number, ReasonCodeGroup>;
  };
  workflowData: WorkflowState['workflowData'];
  services: ServicesByName;
  tags: Tag[];
  outcomes: Outcome[];
  reasonCodeGroups: ReasonCodeGroup[];
  lists: List[];
}) {
  const issuesWithIncomingData: EngineValidationData = {
    actions: [],
    thresholds: [],
  };
  let identifiedObjectsMissingInTheOrganization = [];
  const incomingActions = Object.entries(jsonData.actions);
  const updatedActionsForImport: Record<string, Action> = {};
  const actionPublicIdReassignmentMap = {};
  const rulesPendingMerge: Record<string, Rules> = {};
  const expressionsPendingMerge: Record<string, Expression> = {};

  const {
    actions,
  } = workflowData;
  const existingActions = Object.entries(actions);
  incomingActions.forEach(incomingAction => {
    const actionValue = incomingAction[1];
    // incoming action is those that are copied from somewhere else.
    // the logic below tries to find if there's any action in this workflow
    // where things are being pasted into, that has the same action name.
    // whether it's input/output/outcome/service/tag, they are unique.
    // if there's any action that has exactly same name, it's a matching one
    // and we need to merge data into that existing action.
    let findAnyMatchingExistingAction;
    for (const [existingActionKey, existingActionValue] of existingActions) {
      const ifMatchingTag = existingActionValue.tag?.name?.toLowerCase()
        === actionValue.tag?.name?.toLowerCase()
        && existingActionValue.tag?.name;

      const ifMatchingService = existingActionValue.service?.internalName?.toLowerCase()
        === actionValue.service?.internalName?.toLowerCase()
        && existingActionValue.service?.internalName;

      const ifMatchingOutput = existingActionValue.output?.name?.toLowerCase()
        === actionValue.output?.name?.toLowerCase()
        && existingActionValue.output?.name;

      const ifMatchingInput = existingActionValue.input?.name?.toLowerCase()
        === actionValue.input?.name?.toLowerCase()
        && existingActionValue.input?.name;

      const ifMatchingOutcome = existingActionValue.outcome?.name?.toLowerCase()
        === actionValue.outcome?.name?.toLowerCase()
        && existingActionValue.outcome?.name;

      if (ifMatchingTag || ifMatchingService
        || ifMatchingOutput || ifMatchingInput || ifMatchingOutcome) {
        findAnyMatchingExistingAction = {
          publicId: existingActionKey,
          action: cloneDeep(existingActionValue),
        };
        break;
      }
    }

    if (findAnyMatchingExistingAction?.action) {
      const newAction = findAnyMatchingExistingAction.action;
      if (Object.values(actionValue.rules)?.length
        && [
          ActionType.TAG as string,
          ActionType.SERVICE as string,
          ActionType.OUTCOME as string,
        ].includes(actionValue.type)) {
        rulesPendingMerge[newAction.publicId] = actionValue.rules;
      }

      if (actionValue.output?.expression
        && ActionType.OUTPUT === actionValue.type
        && ActionType.OUTPUT === newAction.type) {
        // we just extend the output expression by adding
        // + operator and the new set of expression onto the very end
        expressionsPendingMerge[newAction.publicId] = actionValue.output.expression;
      }

      Object.entries(actionValue.options).forEach(([actionOptionKey, actionOptionValue]) => {
        if (!newAction.options[actionOptionKey]
          || newAction.options[actionOptionKey] !== actionValue.options[actionOptionKey]) {
          actionOptionValue.publicId = `actionoption__${generatePublicIdHash(20)}`;
          if (actionOptionKey === 'customer_list_id') {
            const remappedListId = remapListIdsForListServiceAction({
              actionOptionValue,
              action: newAction,
              lists,
            });

            if (remappedListId.value) {
              actionOptionValue.value = remappedListId.value;
            }

            if (remappedListId.type) {
              issuesWithIncomingData.actions.push({
                publicId: newAction.publicId,
                type: remappedListId.type,
                manualResolution: remappedListId.type
                  === WorkflowTransformationIssueType.NO_MATCHING_LIST_ID_IN_CUSTOMER,
                data: {
                  [actionOptionKey]: actionOptionValue,
                },
              });
            }
          }

          newAction.options[actionOptionKey] = actionOptionValue;
        }
      });

      updatedActionsForImport[newAction.publicId] = newAction;
      actionPublicIdReassignmentMap[actionValue.publicId] = newAction.publicId;
    } else {
      const newPublicId = `action__${actionValue.type}__${generatePublicIdHash(20)}`;
      actionPublicIdReassignmentMap[actionValue.publicId] = newPublicId;
      actionValue.publicId = newPublicId;

      if (actionValue.options) {
        const { actionOption, issues } = remapActionOptionValues({
          actionOption: actionValue.options,
          action: actionValue,
          lists,
        });

        actionValue.options = actionOption;
        if (issues.length) {
          issuesWithIncomingData.actions = [
            ...issuesWithIncomingData.actions,
            ...issues,
          ];
        }
      }

      const missingObject = generateMissingObjectsInTheOrg({
        action: actionValue,
        tags,
        outcomes,
        services,
      });

      if (missingObject) {
        identifiedObjectsMissingInTheOrganization = [
          ...identifiedObjectsMissingInTheOrganization,
          ...missingObject,
        ];
      }

      updatedActionsForImport[newPublicId] = actionValue;
    }
  });

  const {
    updatedActionsForImportWithRemappedRuleExpressions,
    thresholdsWithReassignedPublicId,
    dataRoutesWithReassignedPublicId,
    rulesPendingMergeWithRemappedId,
    expressionsPendingMergeWithRemappedId,
    identifiedAttributesMissingInTheOrganization,
    issuesWithIncomingThresholds,
  } = remapIdsForThresholdsAndDataRoutes({
    thresholds: Object.entries(jsonData.thresholds),
    dataRoutes: Object.entries(jsonData.dataRoutes),
    actionPublicIdReassignmentMap,
    serviceAttributeData: jsonData.serviceAttributeData,
    services,
    reasonCodeGroups,
    reasonCodeGroupData: jsonData.reasonCodeGroupData,
    updatedActionsForImport,
    rulesPendingMerge,
    expressionsPendingMerge,
  });

  // Validate matrix model RHS actions in imported thresholds
  const matrixModelRHSIssues = collectMatrixModelRHSIssues(
    thresholdsWithReassignedPublicId,
    updatedActionsForImport,
  );

  issuesWithIncomingData.thresholds = [...issuesWithIncomingThresholds, ...matrixModelRHSIssues];
  identifiedObjectsMissingInTheOrganization = [
    ...identifiedObjectsMissingInTheOrganization,
    ...identifiedAttributesMissingInTheOrganization,
  ];

  identifiedObjectsMissingInTheOrganization.sort(
    (a, b) => {
      if (a.type === 'outcome') return -1;
      if (b.type === 'outcome') return 1;
      return 0;
    });

  return {
    actions: updatedActionsForImportWithRemappedRuleExpressions,
    thresholds: thresholdsWithReassignedPublicId,
    dataRoutes: dataRoutesWithReassignedPublicId,
    identifiedObjectsMissingInTheOrganization,
    rulesPendingMerge: rulesPendingMergeWithRemappedId,
    expressionsPendingMerge: expressionsPendingMergeWithRemappedId,
    issues: issuesWithIncomingData,
  };
}
